import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, FileText, RefreshCw, Download } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import {
  listStatements,
  regenerateStatement,
  type FleetStatement
} from '@/api/fleet';
import { formatUsd, formatRev, timeAgo } from '@/lib/format';

const STATUS_BADGE: Record<FleetStatement['status'], string> = {
  ready: 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30',
  pending: 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30',
  regenerating: 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30',
  failed: 'bg-red-500/20 text-red-300 ring-1 ring-red-500/30'
};

export function StatementsScreen() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['fleet-statements'],
    queryFn: listStatements
  });

  const regen = useMutation({
    mutationFn: (id: string) => regenerateStatement(id),
    onSuccess: () => {
      toast.success('Regeneration started');
      qc.invalidateQueries({ queryKey: ['fleet-statements'] });
    },
    onError: (e: Error) => toast.error(e.message)
  });

  const statements = data ?? [];

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="bottom-right" richColors />

      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Statements</h1>
        <p className="mt-1 max-w-2xl text-sm text-nurev-muted">
          Monthly itemized statements (PDF + CSV) broken down by node and
          revenue stream. Use them to bill back internal cost centers, or hand
          them to your accounting team for reconciliation.
        </p>
      </header>

      {isLoading ? (
        <div className="flex items-center gap-3 text-nurev-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading statements…
        </div>
      ) : statements.length === 0 ? (
        <div className="card text-center">
          <FileText className="mx-auto mb-3 h-8 w-8 text-nurev-muted" />
          <p className="text-sm text-nurev-muted">
            No statements yet. They generate automatically at the start of each
            month once you have earnings activity to itemize.
          </p>
        </div>
      ) : (
        <div className="card overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-nurev-border bg-white/[0.02] text-left text-[11px] font-medium uppercase tracking-wider text-nurev-muted">
                <th className="px-4 py-3">Month</th>
                <th className="px-4 py-3 text-right">USD</th>
                <th className="px-4 py-3 text-right">REV</th>
                <th className="px-4 py-3">Generated</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {statements.map(s => (
                <tr key={s._id} className="border-b border-nurev-border/60">
                  <td className="px-4 py-3 font-medium text-nurev-text">
                    {s.monthKey}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-emerald-300">
                    {formatUsd(s.totalUsd)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-nurev-muted">
                    {formatRev(s.totalRev)}
                  </td>
                  <td className="px-4 py-3 text-nurev-muted">
                    {timeAgo(s.generatedAt)}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                        STATUS_BADGE[s.status]
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3">
                      {s.pdfUrl && s.status === 'ready' && (
                        <a
                          href={s.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:underline"
                        >
                          <Download className="h-3 w-3" /> PDF
                        </a>
                      )}
                      {s.csvUrl && s.status === 'ready' && (
                        <a
                          href={s.csvUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-sky-300 hover:underline"
                        >
                          <Download className="h-3 w-3" /> CSV
                        </a>
                      )}
                      <button
                        onClick={() => regen.mutate(s._id)}
                        disabled={regen.isPending || s.status === 'regenerating'}
                        className="inline-flex items-center gap-1 text-xs text-nurev-muted hover:text-nurev-text disabled:opacity-40"
                        title="Regenerate (rate-limited to 1/day)"
                      >
                        <RefreshCw className="h-3 w-3" /> Regen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
